// Builds the agent-facing "current state" payload (SAM-FR-01-001). Pure — reads ONLY from
// RootState (no dispatch, no socket). Merges sessionState (identity / role / permissions / tenant
// — reused, never duplicated) with the uiContext slice (page / mode / visible view).
//
// Permission scoping (SAM-FR-01-006): the snapshot carries the user's own `permissions`, and the
// `view` only ever reflects what the page actually rendered under the user's token. Settings views
// publish catalogue KEYS + the active layer, never raw values — so privileged values can't leak.
import type { RootState } from '@/redux/common/store';
import type { UIView, UIMode, Viewport, ChromeContext, DeviceContext } from '@/redux/slices/uiContextSlice';

export interface UIStateSnapshot {
  session: {
    userId: string | null;
    userName: string | null;
    role: string | null;
    roles: string[];
    roleNames: string[];
    permissions: string[];
    tenantId: string | null;
  };
  ui: {
    path: string;
    pageLabel: string;
    viewport: Viewport;
    /** Always-visible app shell (sidebar nav + signed-in user). */
    chrome: ChromeContext;
    mode: UIMode;
    /** Every content surface currently visible — what the user is actually seeing. */
    surfaces: UIView[];
    /** uiContext.rev — bumps on every screen-state change (card trace `screen_rev`). */
    rev: number;
  };
  /** Browser timezone + coarse geolocation. */
  device: DeviceContext;
  /** ISO timestamp of when the snapshot was captured. */
  capturedAt: string;
}

export function buildUIStateSnapshot(state: RootState): UIStateSnapshot {
  const s = state.sessionState;
  const ui = state.uiContext;
  return {
    session: {
      userId: s.userId,
      userName: s.userName,
      role: s.roles[0] ?? null,
      roles: s.roles,
      roleNames: s.roleNames,
      permissions: s.permissions,
      tenantId: s.tenantId,
    },
    ui: {
      path: ui.path,
      pageLabel: ui.pageLabel,
      viewport: ui.viewport,
      chrome: ui.chrome,
      mode: ui.mode,
      surfaces: ui.surfaces,
      rev: ui.rev,
    },
    device: ui.device,
    capturedAt: new Date().toISOString(),
  };
}

/** The `meta` attached to outbound agent interactions (text send, PTT end, interactive start) so
 *  every turn carries the current situational context + the user's timezone/location. `session`
 *  lets the backend render CURRENT PROFILE (name/role) on a profile page; `selected_user` is the
 *  rename target (focused card / hands-free big card). */
export interface AgentContextMeta {
  user_context: DeviceContext;
  ui: UIStateSnapshot['ui'];
  session: UIStateSnapshot['session'];
  selected_user?: { id: string; name: string; role?: string; status?: string };
}

export function buildAgentContextMeta(state: RootState): AgentContextMeta {
  const snap = buildUIStateSnapshot(state);
  const su = state.uiContext.selectedUser;
  return {
    user_context: snap.device,
    ui: snap.ui,
    session: snap.session,
    ...(su ? { selected_user: su } : {}),
  };
}
